/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Used to create a style hook to utilise JSS
 */
import { AlertTitle, AppBar, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, styled, Tab, Tabs, TextField } from '@mui/material';


/**
 * Used to style buttons.
 */

import {
    IPatient,
    OnlinePatientManagementContext,
    PatientContext,
    useSnackbar
} from '@ngt/opms';


import { JsonServiceClient } from '@servicestack/client';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get the BCT Website URL.
 */

import * as Dtos from '../api/dtos';

import { faUserShield } from '@fortawesome/pro-duotone-svg-icons/faUserShield';

//import AlertTitle from '@material-ui/lab/AlertTitle';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientActiveDirectoryProps {
    canImpersonatePatient: boolean;
    canManagePatientAccount: boolean;
}

interface IPatientActiveDirectoryViewProps {
    onClose: () => void;
    patient?: IPatient | null;
    client: JsonServiceClient;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */
const StyledButton = styled(Button)(({ theme }) => ({
    marginLeft: theme.spacing(3),

    '&:first-of-type': {
        marginLeft: theme.spacing(0)
    }
}))

const StyledTabs = styled(Tabs)(({theme}) => ({
    background: 'white',

    ...theme.applyStyles('dark', {
        background: theme.palette.grey[900],
    })
}))

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const CreateAccount: React.FunctionComponent<IPatientActiveDirectoryViewProps> = ({
    onClose,
    patient,
    client
}) => {
    const [value, setValue] = React.useState('');

    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    }, [setValue]);

    const [password, setPassword] = React.useState('');

    const handlePasswordChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
    }, [setPassword]);

    const { enqueueSnackbar } = useSnackbar();

    const [errorMessage, setErrorMessage] = React.useState('');
    const [updating, setUpdating] = React.useState(false);
    const [error, setError] = React.useState(false);

    const { refresh: patientRefresh } = React.useContext(PatientContext);

    const onClick = React.useCallback(() => {
        if (patient) {
            setError(false);
            setUpdating(true);

            client
                .post(
                    new Dtos.PatientAccountCreate({
                        patientId: patient.id,
                        email: value,
                        password: password
                    })
                )
                .then(_response => {
                    setErrorMessage('');
                    setError(false);
                    setValue('');
                    setPassword('');
                    setUpdating(false);
                    onClose();
                    patientRefresh();
                    enqueueSnackbar(
                        <>
                            <AlertTitle>
                                Patient Account Create
                            </AlertTitle>
                            The patient's account was created successfully.
                        </>,
                        { variant: 'success' }
                    );
                })
                .catch((e) => {
                    setErrorMessage(e.responseStatus.message);
                    setError(true);
                    setUpdating(false);
                    enqueueSnackbar(
                        <>
                            <AlertTitle>
                                Patient Account Not Updated
                            </AlertTitle>
                            An error occurred while attempting to create the patient's account.
                        </>,
                        { variant: 'critical' }
                    );
                })
        }
    }, [setUpdating, setError, setErrorMessage, client, setValue, patient, value, onClose, patientRefresh, setPassword, password, enqueueSnackbar]);


    return (
        <>
            <DialogTitle id="form-dialog-title">Patient Account</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Create an account for the patient to gain access to electronic PRO forms.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    value={value}
                    onChange={handleChange}
                    label="Enter an email address"
                    type="email"
                    fullWidth
                />
                <TextField
                    margin="dense"
                    id="name"
                    value={password}
                    onChange={handlePasswordChange}
                    label="Enter a password"
                    type="password"
                    fullWidth
                />
                {error && (
                    <DialogContentText style={{ color: '#f44336' }}>
                        {errorMessage}
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={onClick} color="primary" disabled={updating}>
                    Create Account
                </Button>
            </DialogActions>
        </>
    );
};

const SetPassword: React.FunctionComponent<IPatientActiveDirectoryViewProps> = ({
    onClose,
    patient,
    client
}) => {
    const [value, setValue] = React.useState('');

    const handleChange = React.useCallback((event: React.ChangeEvent <HTMLInputElement>) => {
        setValue(event.target.value);
    }, [setValue]);

    const { enqueueSnackbar } = useSnackbar();

    const [errorMessage, setErrorMessage] = React.useState('');
    const [updating, setUpdating] = React.useState(false);
    const [error, setError] = React.useState(false);

    const onClick = React.useCallback(() => {
        if (patient) {
            setError(false);
            setUpdating(true);

            client
                .post(
                    new Dtos.PatientAccountSetPassword({
                        patientId: patient.id,
                        password: value
                    })
                )
                .then(_response => {
                    setError(false);
                    setValue('');
                    setUpdating(false);
                    onClose();
                    enqueueSnackbar(
                        <>
                            <AlertTitle>
                                Patient Account Updated
                            </AlertTitle>
                            The patient's password was updated successfully.
                        </>,
                        { variant: 'success' }
                    );
                })
                .catch((e) => {
                    setErrorMessage(e.responseStatus.message);
                    setError(true);
                    setUpdating(false);
                    enqueueSnackbar(
                        <>
                            <AlertTitle>
                                Patient Account Not Updated
                            </AlertTitle>
                            An error occurred while attempting to update the patient's password.
                        </>,
                        { variant: 'critical' }
                    );
                })
        }
    }, [setUpdating, setError, setErrorMessage, client, setValue, patient, value, onClose, enqueueSnackbar]);


    return (
        <>
            <DialogTitle id="form-dialog-title">Set Password</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Set a new password for the patient. The password must be at least 8 characters long.
                </DialogContentText>
                <DialogContentText>
                    The password must:
                    <ul>
                        <li>
                            Be at least 8 characters long
                        </li>
                        <li>
                            Contain at least one upper case letter.
                        </li>
                        <li>
                            Contain at least one lower case letter.
                        </li>
                        <li>
                            Contain at least one number.
                        </li>
                        <li>
                            Container at least one special character (!, @, #, etc).
                        </li>
                    </ul>
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    value={value}
                    onChange={handleChange}
                    label="Enter a new password"
                    type="password"
                    fullWidth
                />
                {error && (
                    <DialogContentText style={{ color: '#f44336' }}>
                        {errorMessage}
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={onClick} color="primary" disabled={updating}>
                    Set Password
                </Button>
            </DialogActions>
        </>
    );
};

const SetEmail: React.FunctionComponent<IPatientActiveDirectoryViewProps> = ({
    onClose,
    patient,
    client
}) => {
    const [value, setValue] = React.useState('');

    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    }, [setValue]);

    const { enqueueSnackbar } = useSnackbar();

    const [errorMessage, setErrorMessage] = React.useState('');
    const [updating, setUpdating] = React.useState(false);
    const [error, setError] = React.useState(false);

    const onClick = React.useCallback(() => {
        const typedPatient = patient as Dtos.Patient;

        if (typedPatient) {
            setError(false);
            setUpdating(true);
            client
                .post(new Dtos.PatientAccountUpdateEmail({
                    patientId: typedPatient.id,
                    email: value
                }))
                .then(_response => {
                    setError(false);
                    setValue('');
                    setUpdating(false);
                    onClose();
                    enqueueSnackbar(
                        <>
                            <AlertTitle>
                                Patient {typedPatient.account ? 'Account' : 'Email'} Updated
                            </AlertTitle>
                            The patient's email was updated successfully.
                        </>,
                        { variant: 'success' }
                    );
                })
                .catch((e) => {
                    setErrorMessage(e.responseStatus.message);
                    setError(true);
                    setUpdating(false);
                    enqueueSnackbar(
                        <>
                            <AlertTitle>
                                Patient {typedPatient.account ? 'Account' : 'Email'} Not Updated
                            </AlertTitle>
                            An error occurred while attempting to update the patient's email.
                        </>,
                        { variant: 'critical' }
                    );
                })
        }
    }, [setUpdating, setError, setErrorMessage, client, setValue, patient, value, onClose, enqueueSnackbar]);


    return (
        <>
            <DialogTitle id="form-dialog-title">Update Email</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Update the email address for the patient.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    value={value}
                    onChange={handleChange}
                    label="Enter a new email address"
                    type="email"
                    fullWidth
                />
                {error && (
                    <DialogContentText style={{ color: '#f44336' }}>
                        {errorMessage}
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={onClick} color="primary" disabled={updating}>
                    Update Email
                </Button>
            </DialogActions>
        </>
    );
};

const ResendEmail: React.FunctionComponent<IPatientActiveDirectoryViewProps> = ({
    onClose,
    patient,
    client
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const [errorMessage, setErrorMessage] = React.useState('');
    const [updating, setUpdating] = React.useState(false);
    const [error, setError] = React.useState(false);

    const onClick = React.useCallback(() => {
        if (patient) {
            setError(false);
            setUpdating(true);
            client
                .post(new Dtos.PatientAccountResendWelcome({
                    patientId: patient.id
                }))
                .then(_response => {
                    setError(false);
                    setUpdating(false);
                    onClose();
                    enqueueSnackbar(
                        <>
                            <AlertTitle>
                                Patient Welcome Sent
                            </AlertTitle>
                            The patient's welcome email was successfully sent.
                        </>,
                        { variant: 'success' }
                    );
                })
                .catch((e) => {
                    setErrorMessage(e.responseStatus.message);
                    setError(true);
                    setUpdating(false);
                    enqueueSnackbar(
                        <>
                            <AlertTitle>
                                Patient Welcome Not Sent
                            </AlertTitle>
                            An error occurred while attempting to send the patient's welcome email.
                        </>,
                        { variant: 'critical' }
                    );
                })
        }
    }, [setUpdating, setError, setErrorMessage, client, patient, onClose, enqueueSnackbar]);


    return (
        <>
            <DialogTitle id="form-dialog-title">Resend Email</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Resend the participant welcome email.
                </DialogContentText>
                <DialogContentText>
                    This email contains a website link to provide the participant with access to the baseline ePROMs questionnaires.
                </DialogContentText>
                <DialogContentText>
                    Resending this email will provide the participant with a new link and will require the participant to update their password.
                </DialogContentText>
                {error && (
                    <DialogContentText style={{ color: '#f44336' }}>
                        {errorMessage}
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={onClick} color="primary" disabled={updating}>
                    Resend Email
                </Button>
            </DialogActions>
        </>
    );
};

const Impersonate: React.FunctionComponent<IPatientActiveDirectoryViewProps> = ({
    onClose,
    patient,
    //client
}) => {
    const onClick = React.useCallback(() => {
        window.location.href = '/auth/openiddict?impersonate=' + patient?.studyNumber;
    }, [patient]);


    return (
        <>
            <DialogTitle id="form-dialog-title">Impersonate</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Impersonate the patient. Impersonating the patient will require reentry of your login details.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={onClick} color="primary">
                    Impersonate
                </Button>
            </DialogActions>
        </>
    );
};


const PatientActiveDirectory: React.FunctionComponent<IPatientActiveDirectoryProps> = (props) => {
    const { data: patient } = React.useContext(PatientContext);

    const onlinePatientManagement = React.useContext(OnlinePatientManagementContext);

    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const [selectedTab, setSelectedTab] = React.useState(props.canManagePatientAccount ? 0 : 1);

    const handleTabChange = React.useCallback((_event: any, newValue: any) => {
        setSelectedTab(newValue);
    }, [setSelectedTab]);

    const typedPatient = patient as Dtos.Patient;

    if (
        patient?.patientStateId !== Dtos.PatientStateType.ConsentPending &&
        patient?.patientStateId !== Dtos.PatientStateType.Consent &&
        patient?.patientStateId !== Dtos.PatientStateType.RadiologistReview &&
        patient?.patientStateId !== Dtos.PatientStateType.CorrectionRequired &&
        patient?.patientStateId !== Dtos.PatientStateType.BctReview &&
        patient?.patientStateId !== Dtos.PatientStateType.Allocation &&
        patient?.patientStateId !== Dtos.PatientStateType.Allocated &&
        patient?.patientStateId !== Dtos.PatientStateType.PostAllocated &&
        patient?.patientStateId !== Dtos.PatientStateType.ThreeMonthsPostSurgery &&
        patient?.patientStateId !== Dtos.PatientStateType.SixMonthsPostSurgery &&
        patient?.patientStateId !== Dtos.PatientStateType.TwelveMonthsPostSurgery &&
        patient?.patientStateId !== Dtos.PatientStateType.TwentyFourMonthsPostSurgery &&
        patient?.patientStateId !== Dtos.PatientStateType.FiveYearsPostSurgery
    ) {
        return null;
    }

    return (
        <>
            {
                (props.canImpersonatePatient || props.canManagePatientAccount) && (
                    <StyledButton
                        variant="contained"
                        color="inherit"
                        onClick={handleClickOpen}
                    >
                        <FontAwesomeIcon icon={faUserShield} fixedWidth />&nbsp;Patient Account
                    </StyledButton>
                )
            }
            <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" maxWidth={typedPatient.account ? "md" : "sm"} fullWidth={typedPatient.account ? false : true}>
                {
                    <>
                        <AppBar
                            position="static"
                            color="primary"
                        >
                            <StyledTabs
                                value={selectedTab}
                                onChange={handleTabChange}
                                variant="scrollable"
                            >
                                {
                                    !!props.canManagePatientAccount && (
                                        <Tab
                                            label="Update Email"
                                            value={0}
                                        />
                                    )
                                }
                                {
                                    !typedPatient.account && patient?.patientStateId > Dtos.PatientStateType.Registration && patient?.patientStateId < Dtos.PatientStateType.ScreenFail&& !!props.canManagePatientAccount && (
                                        <Tab
                                            label="Create Account"
                                            value={1}
                                        />
                                    )
                                }
                                {
                                    !!typedPatient.account && !!props.canImpersonatePatient && (
                                        <Tab
                                            label="Impersonate"
                                            value={2}
                                        />
                                    )
                                }
                                {
                                    !!typedPatient.account && !!props.canManagePatientAccount && (
                                        <Tab
                                            label="Resend Email"
                                            value={3}
                                        />
                                    )
                                }
                                {
                                    !!typedPatient.account && !!props.canManagePatientAccount && (
                                        <Tab
                                            label="Set Password"
                                            value={4}
                                        />
                                    )
                                }
                            </StyledTabs>
                        </AppBar>
                        {
                            selectedTab === 0 && (
                                <SetEmail
                                    client={onlinePatientManagement.serviceStackClient}
                                    onClose={handleClose}
                                    patient={patient}
                                />
                            )
                        }
                        {
                            selectedTab === 1 && (
                                <CreateAccount
                                    client={onlinePatientManagement.serviceStackClient}
                                    onClose={handleClose}
                                    patient={patient}
                                />
                            )
                        }
                        {
                            selectedTab === 2 && (
                                <Impersonate
                                    client={onlinePatientManagement.serviceStackClient}
                                    onClose={handleClose}
                                    patient={patient}
                                />
                            )
                        }
                        {
                            selectedTab === 3 && (
                                <ResendEmail
                                    client={onlinePatientManagement.serviceStackClient}
                                    onClose={handleClose}
                                    patient={patient}
                                />
                            )
                        }
                        {
                            selectedTab === 4 && (
                                <SetPassword
                                    client={onlinePatientManagement.serviceStackClient}
                                    onClose={handleClose}
                                    patient={patient}
                                />
                            )
                        }
                    </>
                }
            </Dialog>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default PatientActiveDirectory;